import { Select, Trans, useLingui } from '@lingui/react/macro';
import type { HTMLAttributes } from 'react';
import { useEffect, useMemo } from 'react';

import { Typography } from '@remarkable/ark-web';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  useCurrentMember,
  usePaymentIntervals,
  useStripeSubscription,
} from 'src/api/queries';
import {
  Button,
  Divider,
  FlexDivider,
  Modal,
  Sheet,
  Spinner,
  TextWithLabel,
} from 'src/components';
import {
  formatAmount,
  formatAmountBrowserDefaultLocale,
} from 'src/utils/productUtils';

import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { getRouteApi } from '@tanstack/react-router';
import { Boundary } from 'src/components/Boundary';
import { Link2 } from 'src/components/Link2';
import { capitalize } from 'src/utils/capitalize';
import { useOfferExpirationDetails } from '../utils/getOfferExpirationDetails';
import { useHasClickedBiennialOffer } from '../utils/hasBiennialOfferStore';
import { CardHeading } from './CardHeading';
import { ChangePaymentIntervalToAnnualForm } from './ChangePaymentIntervalToAnnualForm';
import { ChangePaymentIntervalToBiennialForm } from './ChangePaymentIntervalToBiennialForm';
import { ChangePaymentIntervalToMonthlyForm } from './ChangePaymentIntervalToMonthlyForm';

export const ROUTE_API_PATH = '/_auth/_layout/subscriptions/payment';

const routeApi = getRouteApi(ROUTE_API_PATH);

export const ChangePaymentInterval = ({
  componentLocation,
  ...props
}: HTMLAttributes<HTMLDivElement> & { componentLocation?: string }) => {
  const { t, i18n } = useLingui();
  const subscription = useStripeSubscription();
  const seat = useCurrentMember();
  const paymentIntervals = usePaymentIntervals();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const [hasClickedBiennialOffer, setHasClickedBiennialOffer] =
    useHasClickedBiennialOffer();
  const biennialOfferHasEnded = useOfferExpirationDetails().offerHasEnded;
  const hasBiennialOffer = useMemo(() => {
    const hasBiennialOption = !!paymentIntervals.data?.options.biennial;

    return (
      hasClickedBiennialOffer &&
      hasBiennialOption &&
      biennialOfferHasEnded === false &&
      !!subscription.data?.paymentInformation?.paymentInterval &&
      subscription.data.paymentInformation.paymentInterval !== 'biennial'
    );
  }, [
    biennialOfferHasEnded,
    hasClickedBiennialOffer,
    paymentIntervals.data,
    subscription.data,
  ]);

  useEffect(() => {
    if (!paymentIntervals.data) return;

    if (search.updateBillingInterval === 'biennial') {
      setHasClickedBiennialOffer(true);
    }

    if (search.updateBillingInterval === 'true') {
      void navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          updateBillingInterval: paymentIntervals.data?.isAnnual
            ? 'monthly'
            : 'annual',
        }),
      });
    }
  }, [search.updateBillingInterval, navigate, paymentIntervals.data]);

  if (!subscription.data || !seat || !paymentIntervals.data) return null;

  if (paymentIntervals.data.canChangePaymentInterval === false) return null;

  if (paymentIntervals.data.optionsList.length < 1) return null;

  const annualPrice = formatAmountBrowserDefaultLocale(
    paymentIntervals.data.options.annually.amount.total ?? 0,
    paymentIntervals.data.options.annually.amount.currency
  );

  const monthlyPrice = formatAmountBrowserDefaultLocale(
    paymentIntervals.data.options.monthly.amount.total ?? 0,
    paymentIntervals.data.options.monthly.amount.currency
  );

  const switchToMonthly = !paymentIntervals.data.isMonthly;
  const confirmText = switchToMonthly
    ? t`Switch to monthly billing`
    : t`Switch to annual billing`;

  const onClose = (text: string) => () => {
    if (componentLocation) {
      tracker.trackEvent(
        new ButtonClicked({
          component_location:
            ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL,
          text,
          action: 'close change payment interval modal',
        })
      );
    }
  };

  return (
    <Sheet {...props}>
      <CardHeading>
        <Trans>Payment interval</Trans>
      </CardHeading>

      <Divider />

      <div className="flex w-full flex-col flex-wrap justify-between gap-16 lm:flex-row">
        <TextWithLabel label={t`Current interval`} className="">
          <Select
            value={paymentIntervals.data.currentInterval.interval}
            _monthly="Monthly"
            _annually="Annually"
            _biennial="2-year"
            other={capitalize(paymentIntervals.data.currentInterval.interval)}
          />
        </TextWithLabel>

        <FlexDivider />

        {paymentIntervals.data.isMonthly && (
          <>
            <TextWithLabel
              label={t`Price per month`}
              className="flex-1 shrink-0 whitespace-nowrap"
            >
              {formatAmount(
                paymentIntervals.data.options.monthly.amount.total ?? 0,
                paymentIntervals.data.options.monthly.amount.currency,
                i18n.locale
              )}{' '}
              <span className="font-light">
                (
                {formatAmount(
                  (paymentIntervals.data.options.monthly.amount.total ?? 0) *
                    12,
                  paymentIntervals.data.options.monthly.amount.currency,
                  i18n.locale
                )}
                <Trans context="Pricing period label">/year</Trans>)
              </span>
            </TextWithLabel>
          </>
        )}

        {paymentIntervals.data.isAnnual && (
          <>
            <TextWithLabel label={t`Price per year`} className="">
              {formatAmount(
                paymentIntervals.data.options.annually.amount.total ?? 0,
                paymentIntervals.data.options.annually.amount.currency,
                i18n.locale
              )}{' '}
              <span className="font-light">
                (
                {formatAmount(
                  (paymentIntervals.data.options.annually.amount.total ?? 0) /
                    12,
                  paymentIntervals.data.options.annually.amount.currency,
                  i18n.locale
                )}
                <Trans context="Pricing period label">/month</Trans>)
              </span>
            </TextWithLabel>
          </>
        )}

        {paymentIntervals.data.isBiennial && (
          <>
            <TextWithLabel label={t`Price per 2 years`} className="flex-1">
              {formatAmount(
                paymentIntervals.data.options.biennial.amount.total ?? 0,
                paymentIntervals.data.options.biennial.amount.currency,
                i18n.locale
              )}{' '}
              <span className="font-light">
                (
                {formatAmount(
                  (paymentIntervals.data.options.biennial.amount.total ?? 0) /
                    24,
                  paymentIntervals.data.options.biennial.amount.currency,
                  i18n.locale
                )}
                <Trans context="Pricing period label">/month</Trans>)
              </span>
            </TextWithLabel>
          </>
        )}

        <div className="flex flex-col items-start gap-8 lm:flex-row">
          <Modal.Root
            open={
              search.updateBillingInterval === 'annual' ||
              search.updateBillingInterval === 'monthly' ||
              search.updateBillingInterval === 'biennial'
            }
            onOpenChange={(open) => {
              if (open) {
                const nextInterval = switchToMonthly ? 'monthly' : 'annual';

                void navigate({
                  to: '.',
                  search: (prev) => ({
                    ...prev,
                    updateBillingInterval: nextInterval,
                  }),
                });
              } else {
                void navigate({
                  to: '.',
                  search: (prev) => ({
                    ...prev,
                    updateBillingInterval: undefined,
                  }),
                });
                onClose('close change payment interval modal');
              }
            }}
          >
            <Modal.Trigger asChild>
              <Button
                onClick={() => {
                  if (componentLocation) {
                    tracker.trackEvent(
                      new ButtonClicked({
                        component_location: componentLocation,
                        text: confirmText,
                        action: 'open change payment interval modal',
                      })
                    );
                  }
                }}
                variant={
                  paymentIntervals.data.isMonthly
                    ? hasBiennialOffer
                      ? 'tertiary'
                      : 'primary'
                    : 'tertiary'
                }
                disabled={subscription.isPending || subscription.data?.readonly}
                size="medium"
                className="w-full ls:w-fit"
                data-cy="change-payment-interval-button"
              >
                {confirmText}
              </Button>
            </Modal.Trigger>

            <Modal.Content>
              <Boundary
                loaderComponent={
                  <>
                    <Modal.Title>
                      <Spinner />
                    </Modal.Title>
                    <VisuallyHidden>
                      <Modal.Description>
                        <Trans>Loading billing information...</Trans>
                      </Modal.Description>
                    </VisuallyHidden>
                  </>
                }
              >
                {search.updateBillingInterval === 'monthly' && (
                  <ChangePaymentIntervalToMonthlyForm />
                )}
                {search.updateBillingInterval === 'annual' && (
                  <ChangePaymentIntervalToAnnualForm />
                )}
                {search.updateBillingInterval === 'biennial' && (
                  <ChangePaymentIntervalToBiennialForm />
                )}
              </Boundary>
            </Modal.Content>
          </Modal.Root>
          {hasBiennialOffer && (
            <Link2
              as="button"
              to="."
              search={{ updateBillingInterval: 'biennial' }}
              componentLocation={
                ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
              }
              size="medium"
              className="w-full ls:w-fit"
              variant="primary"
            >
              <Trans>Switch to 2-year billing</Trans>
            </Link2>
          )}
        </div>
      </div>

      {paymentIntervals.data.isMonthly && !hasBiennialOffer && (
        <Typography
          as="p"
          variant="body-sm"
          className="text-center font-regular italic xs:text-left"
        >
          <Trans>
            Pay{' '}
            <b className="font-bold" data-cy="annual-price">
              {annualPrice}/year
            </b>{' '}
            <span className="whitespace-nowrap">and get two months free.</span>
          </Trans>
        </Typography>
      )}

      {paymentIntervals.data.isAnnual && !hasBiennialOffer && (
        <Typography
          as="p"
          variant="body-sm"
          className="text-center font-regular italic xs:text-left"
        >
          <Trans>
            <span>Want to pay monthly?</span>
            &nbsp; Pay{' '}
            <b className="font-bold" data-cy="monthly-price">
              {monthlyPrice}/month
            </b>
          </Trans>
        </Typography>
      )}
    </Sheet>
  );
};
